/**
 * Addressbook Domain Types
 * Based on Strapi api::location.location schema
 */

// ============================================================================
// i18n Support
// ============================================================================

/**
 * Localized text field (supports English and Arabic)
 */
export interface LocalizedText {
  en: string;
  ar?: string;
}

// ============================================================================
// Core Domain Entities
// ============================================================================

/**
 * Location status
 */
export type LocationStatus = 'active' | 'inactive';

/**
 * Contact type
 */
export type ContactType =
  | 'gate_manager'
  | 'dock_supervisor'
  | 'security'
  | 'primary_contact'
  | 'emergency_contact';

/**
 * Factory summary for city context (minimal fields)
 */
export interface FactorySummaryRef {
  id: string;
  documentId: string;
  code: string;
  name: string;
}

/**
 * City entity (from Strapi api::city.city)
 */
export interface City {
  id: string;
  documentId: string;
  code: string;
  name: string;
  stateProvince?: string;
  // Region relation (optional, for reference/grouping only)
  regionId?: string;
  regionDocumentId?: string;
  regionName?: string;
  countryId?: string;
  // Factories that serve this city (optional, populated when needed)
  factories?: FactorySummaryRef[];
  isActive: boolean;
}

/**
 * Region entity (from Strapi api::region.region)
 */
export interface Region {
  id: string;
  documentId: string;
  code: string;
  name: string;
  countryId?: string;
  factoryIds?: string[];
  factoryNames?: string[];
  isActive: boolean;
}

/**
 * Country entity (from Strapi api::country.country)
 */
export interface Country {
  id: string;
  documentId: string;
  code: string; // ISO 3-letter code
  name: string;
  isActive: boolean;
}

/**
 * Tag entity (from Strapi api::tag.tag)
 */
export interface Tag {
  id: string;
  documentId: string;
  code: string;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  categoryId?: string;
  sortOrder: number;
  isActive: boolean;
}

/**
 * Tag Category entity (from Strapi api::tag-category.tag-category)
 */
export interface TagCategory {
  id: string;
  documentId: string;
  code: string;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  sortOrder: number;
  isActive: boolean;
  allowsMultiple: boolean;
  isRequired: boolean;
}

/**
 * Location Type entity (from Strapi api::location-type.location-type)
 */
export interface LocationType {
  id: string;
  documentId: string;
  code: string;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  sortOrder: number;
  isActive: boolean;
  requiresParent: boolean;
  requiresCoordinates: boolean;
  requiresLoadingCapabilities: boolean;
  requiresOperatingHours: boolean;
  requiresPermits: boolean;
  allowedChildTypes?: string[];
  allowedOwnerTypes?: string[];
  hierarchyLevelRestriction?: number;
}

/**
 * Contact entity (from Strapi api::contact.contact)
 */
export interface Contact {
  id: string;
  documentId: string;
  name: string;
  title?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  fax?: string;
  contactType: ContactType;
  preferredLanguage?: string;
  notes?: string;
  isPrimary: boolean;
  isActive: boolean;
  locationId?: string;
  tenantId?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Tenant entity (from Strapi api::tenant.tenant)
 */
export interface Tenant {
  id: string;
  documentId: string;
  code: string;
  name: string;
  legalName?: string;
  taxId?: string;
  vatNumber?: string;
  registrationNumber?: string;
  status: 'pending' | 'active' | 'inactive' | 'suspended';
  settings?: Record<string, unknown>;
}

/**
 * Coordinates (derived from latitude/longitude)
 */
export interface Coordinates {
  lat: number;
  lng: number;
}

/**
 * Location entity (from Strapi api::location.location)
 * This is the main addressbook entity
 */
export interface Location {
  id: string;
  documentId: string;
  code: string;
  name: string;
  addressLine1?: string;
  addressLine2?: string;
  postalCode?: string;
  timezone?: string;
  latitude?: number;
  longitude?: number;
  status: LocationStatus;
  metadata?: Record<string, unknown>;
  // Relations
  city?: City;
  country?: Country;
  locationType?: LocationType;
  parent?: Location;
  tenant?: Tenant;
  tags?: Tag[];
  contacts?: Contact[];
  // Timestamps
  createdAt: string;
  updatedAt: string;
}

// ============================================================================
// Derived Types for UI (mapped from Location)
// ============================================================================

/**
 * Address type for backward compatibility with UI components
 * Maps LocationType codes to simplified types
 */
export type AddressType =
  | 'site'
  | 'office_location'
  | 'warehouse'
  | 'home'
  | 'plant'
  | 'customer';

/**
 * Address - UI-friendly representation of a Location
 */
export interface Address {
  id: string;
  documentId: string;
  code: string;
  /** Resolved display name for current locale */
  name: string;
  /** Full i18n for edit forms (Strapi localized `name`) */
  nameI18n?: LocalizedText;
  type: AddressType;
  // Flattened address fields
  address: string; // addressLine1 + addressLine2
  addressLine1?: string;
  addressLine1I18n?: LocalizedText;
  addressLine2?: string;
  addressLine2I18n?: LocalizedText;
  city: string; // city.name
  cityId?: string; // city.id (for form binding)
  cityDocumentId?: string; // city.documentId (stable across locales)
  cityCode?: string;
  region?: string; // region.name (fallback to city.stateProvince)
  regionId?: string;
  regionDocumentId?: string; // region.documentId (stable across locales)
  country: string; // country.name
  countryId?: string; // country.id (for form binding)
  countryCode?: string; // country.code
  postalCode?: string;
  timezone?: string;
  // Coordinates
  coordinates?: Coordinates;
  // Relations
  locationType?: LocationType;
  tags?: Tag[];
  contacts?: Contact[];
  tenant?: { name: string; code: string };
  // Metadata
  contactName?: string; // from primary contact or metadata
  contactPhone?: string;
  contactEmail?: string;
  notes?: string; // from primary contact or metadata
  isDefault?: boolean; // from metadata
  isFavorite?: boolean; // from metadata
  // Status
  status: LocationStatus;
  isActive: boolean;
  // Timestamps
  createdAt: string;
  updatedAt: string;
}

// ============================================================================
// Stats & Filters
// ============================================================================

/**
 * Address statistics
 * Note: byType uses location type codes from API, not the mapped AddressType
 */
export interface AddressStats {
  total: number;
  active: number;
  inactive: number;
  sites?: number;
  offices?: number;
  byType: Record<string, number>;
}

/**
 * Address filter criteria
 */
export interface AddressesFilter {
  type?: AddressType | 'all';
  locationTypeCode?: string;
  status?: LocationStatus | 'all';
  search?: string;
  city?: string;
  cityId?: string;
  countryCode?: string;
  tags?: string[];
  tenantId?: string;
  page?: number;
  pageSize?: number;
}

// ============================================================================
// Pagination Types
// ============================================================================

export interface PaginationMeta {
  page: number;
  pageSize: number;
  pageCount: number;
  total: number;
}

export interface PaginatedAddresses {
  data: Address[];
  pagination: PaginationMeta;
}

// ============================================================================
// Request Types
// ============================================================================

/**
 * Create address request
 */
export interface CreateAddressRequest {
  code?: string; // auto-generated if not provided
  name: LocalizedText;
  type?: AddressType;
  locationTypeId?: string;
  addressLine1: LocalizedText;
  addressLine2?: LocalizedText;
  cityId?: string;
  countryId?: string;
  postalCode?: string;
  timezone?: string;
  coordinates?: Coordinates;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  notes?: string;
  isDefault?: boolean;
  tags?: string[]; // tag documentIds
  metadata?: Record<string, unknown>;
}

/**
 * Update address request
 */
export interface UpdateAddressRequest {
  name?: LocalizedText;
  type?: AddressType;
  locationTypeId?: string;
  addressLine1?: LocalizedText;
  addressLine2?: LocalizedText;
  cityId?: string;
  countryId?: string;
  postalCode?: string;
  timezone?: string;
  coordinates?: Coordinates | null;
  contactName?: string | null;
  contactPhone?: string;
  contactEmail?: string;
  notes?: string | null;
  isDefault?: boolean;
  isFavorite?: boolean;
  status?: LocationStatus;
  tags?: string[];
  metadata?: Record<string, unknown>;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Location type code to AddressType mapping
 */
export const LOCATION_TYPE_TO_ADDRESS_TYPE: Record<string, AddressType> = {
  site: 'site',
  office: 'office_location',
  warehouse: 'warehouse',
  home: 'home',
  plant: 'plant',
  customer: 'customer',
  customer_site: 'site',
  delivery_point: 'site',
};

/**
 * AddressType to LocationType code mapping
 */
export const ADDRESS_TYPE_TO_LOCATION_TYPE: Record<AddressType, string> = {
  site: 'site',
  office: 'office',
  warehouse: 'warehouse',
  home: 'home',
  plant: 'plant',
  customer: 'customer',
};
